/**
 * AP-PORTAL-01: Supplier Login Page
 * Route: /portal/login
 *
 * Dark cinematic design matching Prashasta premium brand.
 * Uses httpOnly cookie auth — no localStorage token storage.
 */
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, LogIn, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SupplierLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/portal/dashboard";

  async function handleLogin(e) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${API}/api/portal/auth/login`,
        { email: email.trim().toLowerCase(), password },
        { withCredentials: true }
      );
      navigate(from, { replace: true });
    } catch (err) {
      const msg =
        err.response?.data?.detail || "Unable to sign in. Please try again.";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      data-testid="supplier-login-page"
      style={{
        minHeight: "100vh",
        background: "#111010",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'DM Sans', sans-serif",
      }}
    >
      {/* Brand mark */}
      <div style={{ marginBottom: "40px", textAlign: "center" }}>
        <p
          style={{
            fontSize: "11px",
            letterSpacing: "4px",
            textTransform: "uppercase",
            color: "#C9A15F",
            marginBottom: "8px",
          }}
        >
          Prashasta Events
        </p>
        <h1
          style={{
            fontFamily: "'Cormorant Garamond', Georgia, serif",
            fontSize: "clamp(28px, 5vw, 38px)",
            fontWeight: 600,
            color: "#F5EFE6",
            margin: 0,
            lineHeight: 1.2,
          }}
        >
          Partner Portal
        </h1>
        <p
          style={{
            fontSize: "13px",
            color: "#8A7A6A",
            marginTop: "6px",
          }}
        >
          Secure supplier access
        </p>
      </div>

      {/* Login card */}
      <div
        style={{
          width: "100%",
          maxWidth: "420px",
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(201,161,95,0.2)",
          borderRadius: "16px",
          padding: "clamp(24px, 5vw, 40px)",
          backdropFilter: "blur(12px)",
        }}
      >
        <form onSubmit={handleLogin} data-testid="portal-login-form">
          {/* Email */}
          <div style={{ marginBottom: "20px" }}>
            <label
              htmlFor="portal-email"
              style={{ display: "block", fontSize: "12px", color: "#A89880", marginBottom: "6px", letterSpacing: "0.5px" }}
            >
              Email address
            </label>
            <input
              id="portal-email"
              data-testid="portal-email-input"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              style={{
                width: "100%",
                padding: "12px 16px",
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px",
                color: "#F5EFE6",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
                transition: "border-color 0.2s",
              }}
              onFocus={(e) => (e.target.style.borderColor = "rgba(201,161,95,0.6)")}
              onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")}
            />
          </div>

          {/* Password */}
          <div style={{ marginBottom: "24px" }}>
            <label
              htmlFor="portal-password"
              style={{ display: "block", fontSize: "12px", color: "#A89880", marginBottom: "6px", letterSpacing: "0.5px" }}
            >
              Password
            </label>
            <div style={{ position: "relative" }}>
              <input
                id="portal-password"
                data-testid="portal-password-input"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                style={{
                  width: "100%",
                  padding: "12px 44px 12px 16px",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "8px",
                  color: "#F5EFE6",
                  fontSize: "14px",
                  outline: "none",
                  boxSizing: "border-box",
                  transition: "border-color 0.2s",
                }}
                onFocus={(e) => (e.target.style.borderColor = "rgba(201,161,95,0.6)")}
                onBlur={(e) => (e.target.style.borderColor = "rgba(255,255,255,0.12)")}
              />
              <button
                type="button"
                data-testid="toggle-password-visibility"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: "#6B5B4A",
                  padding: "4px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            <div style={{ textAlign: "right", marginTop: "8px" }}>
              <Link
                to="/portal/forgot-password"
                data-testid="portal-forgot-password-link"
                style={{
                  fontSize: "12px",
                  color: "#C9A15F",
                  textDecoration: "none",
                  opacity: 0.8,
                }}
              >
                Forgot password?
              </Link>
            </div>
          </div>

          {/* Error */}
          {error && (
            <div
              data-testid="portal-login-error"
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: "8px",
                padding: "12px",
                background: "rgba(220,50,50,0.1)",
                border: "1px solid rgba(220,50,50,0.3)",
                borderRadius: "8px",
                marginBottom: "16px",
              }}
            >
              <AlertCircle size={14} style={{ color: "#E57373", flexShrink: 0, marginTop: "1px" }} />
              <span style={{ fontSize: "13px", color: "#E57373", lineHeight: 1.4 }}>{error}</span>
            </div>
          )}

          {/* Submit */}
          <button
            type="submit"
            data-testid="portal-login-submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "13px 24px",
              background: loading ? "rgba(201,161,95,0.4)" : "#C9A15F",
              color: "#111010",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: loading ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              transition: "background 0.2s",
              letterSpacing: "0.3px",
            }}
          >
            {loading ? (
              <>
                <div
                  style={{
                    width: "14px",
                    height: "14px",
                    border: "2px solid rgba(17,16,16,0.3)",
                    borderTopColor: "#111010",
                    borderRadius: "50%",
                    animation: "spin 0.8s linear infinite",
                  }}
                />
                Signing in…
              </>
            ) : (
              <>
                <LogIn size={15} />
                Sign In to Portal
              </>
            )}
          </button>
        </form>
      </div>

      {/* Footer */}
      <p
        style={{
          marginTop: "32px",
          fontSize: "12px",
          color: "#4A3A2A",
          textAlign: "center",
        }}
      >
        Authorised suppliers only. Access is logged and audited.
      </p>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        input::placeholder { color: #4A3A2A; }
      `}</style>
    </div>
  );
}
